import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["questionKey"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { useCallback, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { BUYER_INTENT, DATA_ENRICHMENT, FORM_SHORTENING } from 'self-service-api/constants/Features';
import BannerSection from '../components/banners/BannerSection';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import { ERROR_SEVERITY } from '../components/error-boundary/error-severity';
import { useErrorBoundary } from '../components/error-boundary/useErrorBoundary';
import withErrorBoundary from '../components/error-boundary/withErrorBoundary';
import Faq from '../components/faqs/Faq';
import Question from '../components/faqs/Question';
import JuneBreezeIntelligencePackInfo from '../components/hubspot-credits/JuneBreezeIntelligencePackInfo';
import LearnAboutCreditsSection from '../components/hubspot-credits/LearnAboutCreditsSection';
import KeyFeatures from '../components/key-features/KeyFeatures';
import UBBKeyFeatures from '../components/key-features/UBBKeyFeatures';
import HeaderLayoutWrapper from '../components/layout/HeaderLayoutWrapper';
import LegacyBreezeIntelligencePackInfo from '../components/merch-card/LegacyBreezeIntelligencePackInfo';
import { BREEZE_INTELLIGENCE_PAGE } from '../constants/ErrorBoundaryNames';
import { OWNERSHIP_CHECK_FAILED, PRICING_AND_PURCHASING_FAILED_STATUS } from '../constants/ErrorCauses';
import { FaqsConfig, JuneFaqsConfig } from '../constants/faqs/FaqsConfig';
import { NavItemEnum } from '../constants/routing/NavItems';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useRedirectIfIneligible } from '../hooks/useRedirectIfIneligible';
import { useAppSelector } from '../reducers';
import { isUngatedForBreezeIntelligenceSelector, isUngatedForHubSpotCreditsSelector } from '../selectors/GatingSelectors';
import { hasGatesLoadedSelector, hasOnlyOwnershipCheckFailedSelector, hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { trackPageView } from '../utils/tracking/trackingEventUtils';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const features = [DATA_ENRICHMENT, BUYER_INTENT, FORM_SHORTENING];
const BreezeIntelligencePage = () => {
  useAnchorLinkScroll();
  useDocumentTitle('breeze-intelligence');
  const navItem = NavItemEnum.BREEZE_INTELLIGENCE;
  const isUngatedForBreezeIntelligence = useAppSelector(isUngatedForBreezeIntelligenceSelector);
  const isUngatedForHubSpotCredits = useAppSelector(isUngatedForHubSpotCreditsSelector);
  const faqsConfig = (isUngatedForHubSpotCredits ? JuneFaqsConfig : FaqsConfig)[navItem];
  const isAppReady = useGlobalLoading();
  const hasPricingAndPurchasingFailed = useAppSelector(hasPricingAndPurchasingFailedSelector);
  const hasOnlyOwnershipCheckFailed = useAppSelector(hasOnlyOwnershipCheckFailedSelector);
  const hasGatesLoaded = useAppSelector(hasGatesLoadedSelector);
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const {
    showBoundary
  } = useErrorBoundary();

  /**
   * On public, there's a race condition where pricing and purchasing has succeeded, but the activeFeatureFlags have not been populated yet (@see PublicMetaDataActions). So sometimes this condition gets evaluated to true and redirects to /suite before we actually confirm if the user is gated for Breeze Intelligence.
   */
  useRedirectIfIneligible({
    condition: useCallback(() => {
      if (!hasGatesLoaded) {
        return;
      }
      return !isUngatedForBreezeIntelligence;
    }, [hasGatesLoaded, isUngatedForBreezeIntelligence])
  });
  useEffect(() => {
    getUsageTracker().setProperties({
      defaultedTab: '',
      tab: ''
    });
    trackPageView({
      screen: navItem
    });
  }, [navItem]);
  if (hasPricingAndPurchasingFailed) {
    if (hasOnlyOwnershipCheckFailed) {
      showBoundary(new Error(OWNERSHIP_CHECK_FAILED));
    }
    showBoundary(new Error(PRICING_AND_PURCHASING_FAILED_STATUS));
  }
  if (!isAppReady || !hasGatesLoaded) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    });
  }
  return /*#__PURE__*/_jsxs(HeaderLayoutWrapper, {
    navItem: navItem,
    navMarkerName: "BREEZE_INTELLIGENCE_PAGE",
    children: [/*#__PURE__*/_jsx(BannerSection, {
      screen: navItem
    }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
      children: [/*#__PURE__*/_jsx(ProductPageTitle, {
        nonProductIdentifier: navItem
      }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
        nonProductIdentifier: navItem
      })]
    }), isUngatedForHubSpotCredits ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(JuneBreezeIntelligencePackInfo, {
        screen: navItem
      }), /*#__PURE__*/_jsx(UBBKeyFeatures, {
        navItem: navItem
      }), /*#__PURE__*/_jsx(LearnAboutCreditsSection, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.pages.hubspot-credits.learn.header.breeze"
        })
      })]
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(LegacyBreezeIntelligencePackInfo, {
        screen: navItem
      }), /*#__PURE__*/_jsx(KeyFeatures, {
        features: features
      })]
    }), /*#__PURE__*/_jsx(Faq, {
      children: faqsConfig.map(_ref => {
        let {
            questionKey
          } = _ref,
          passthroughOptions = _objectWithoutPropertiesLoose(_ref, _excluded);
        return /*#__PURE__*/_jsx(Question, Object.assign({
          questionKey: questionKey
        }, passthroughOptions), questionKey);
      })
    })]
  });
};
export default withErrorBoundary(BreezeIntelligencePage, {
  fallback: /*#__PURE__*/_jsx(Redirect, {
    to: createToObject('/suite')
  }),
  errorBoundaryName: BREEZE_INTELLIGENCE_PAGE,
  severity: ERROR_SEVERITY.HIGH
});